import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";

export const scrollTo = (hash) => {
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(hash, { offset: -72 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = ({ t, lang, setLang, dark, setDark }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "facilitator", label: t.nav.facilitator, hash: "#facilitateur", testid: "nav-link-facilitator" },
    { id: "fiction", label: t.nav.fiction, hash: "#fiction", testid: "nav-link-fiction" },
    { id: "contact", label: t.nav.contact, hash: "#contact", testid: "nav-link-contact" },
  ];

  const go = (hash) => { setOpen(false); scrollTo(hash); };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl border-b border-line" : ""
      }`}
      style={scrolled ? { backgroundColor: "var(--glass)" } : undefined}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-[72px] flex items-center justify-between">
        <button
          data-testid="navbar-logo-link"
          onClick={() => go("#top")}
          className="text-left leading-none group"
        >
          <span className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-ink group-hover:text-brass transition-colors duration-300">
            Thomas Lubeau
          </span>
          <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.25em] text-ink3 mt-1">
            {t.nav.tagline}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.hash)}
              className="text-sm text-ink2 hover:text-ink transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-1 border border-line rounded-full p-1">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid="language-toggle-button"
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 rounded-full font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-ink text-paper" : "text-ink3 hover:text-ink"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="theme-toggle-button"
            onClick={() => setDark(!dark)}
            aria-label="Theme"
            className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-ink2 hover:text-ink hover:border-linestrong transition-colors duration-300"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-ink"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="md:hidden border-b border-line backdrop-blur-xl px-6 py-6 flex flex-col gap-4"
            style={{ backgroundColor: "var(--glass)" }}
          >
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-${l.testid}`}
                onClick={() => go(l.hash)}
                className="text-left font-serif text-2xl text-ink"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center gap-1 border border-line rounded-full p-1">
                {["fr", "en"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-2.5 py-1 rounded-full font-mono text-[11px] uppercase tracking-widest ${
                      lang === l ? "bg-ink text-paper" : "text-ink3"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setDark(!dark)}
                className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-ink2"
                aria-label="Theme"
              >
                {dark ? <Sun size={15} /> : <Moon size={15} />}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
